import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/apiService';
import './Dashboard.css';

const Dashboard = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [systemInfo, setSystemInfo] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [systemLogs, setSystemLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Sadece kimlik doğrulama tamamlandığında ve kullanıcı giriş yapmışsa veri yükle
    if (!authLoading && isAuthenticated) {
      loadDashboardData();
    }
  }, [authLoading, isAuthenticated]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Paralel olarak tüm verileri yükle
      const [systemInfoData, tenantsData, integrationsData, logsData] = await Promise.all([
        apiService.getSystemInfo(),
        apiService.getTenants(),
        apiService.getIntegrations(),
        apiService.getSystemLogs(10)
      ]);

      setSystemInfo(systemInfoData);
      setTenants(tenantsData);
      setIntegrations(integrationsData);
      setSystemLogs(logsData);
    } catch (error) {
      console.error('Dashboard data loading error:', error);
      setError('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'green';
      case 'inactive':
        return 'red';
      case 'pending':
        return 'orange';
      default:
        return 'gray';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('tr-TR');
  };

  // Kimlik doğrulama yükleniyorsa bekle
  if (authLoading) {
    return (
      <div className="dashboard">
        <div className="loading">Kimlik doğrulama kontrol ediliyor...</div>
      </div>
    );
  }

  // Kullanıcı giriş yapmamışsa hata göster
  if (!isAuthenticated) {
    return (
      <div className="dashboard">
        <div className="error">Lütfen önce giriş yapın.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading">Yükleniyor...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <div className="error">{error}</div>
        <button onClick={loadDashboardData}>Tekrar Dene</button>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <h1>ASTOS Sistem Yönetim Paneli</h1>
      
      {/* Sistem Bilgileri */}
      <div className="dashboard-section">
        <h2>Sistem Bilgileri</h2>
        <div className="system-info-grid">
          <div className="info-card">
            <h3>Versiyon</h3>
            <p>{systemInfo?.version || 'N/A'}</p>
          </div>
          <div className="info-card">
            <h3>Aktif Kullanıcılar</h3>
            <p>{systemInfo?.activeUsers || 0}</p>
          </div>
          <div className="info-card">
            <h3>Toplam Firma</h3>
            <p>{systemInfo?.totalTenants || 0}</p>
          </div>
          <div className="info-card">
            <h3>Entegrasyonlar</h3>
            <p>{systemInfo?.totalIntegrations || 0}</p>
          </div>
          <div className="info-card">
            <h3>Son Loglar</h3>
            <p>{systemInfo?.recentLogs || 0}</p>
          </div>
          <div className="info-card">
            <h3>AI Modelleri</h3>
            <p>{systemInfo?.aiModels?.length || 0}</p>
          </div>
        </div>
      </div>

      {/* Firmalar */}
      <div className="dashboard-section">
        <h2>Firmalar (Tenants)</h2>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Firma Adı</th>
                <th>Domain</th>
                <th>E-posta</th>
                <th>Telefon</th>
                <th>Durum</th>
                <th>Plan</th>
                <th>Kullanıcı Sayısı</th>
                <th>Entegrasyon Sayısı</th>
                <th>Oluşturulma Tarihi</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((tenant) => (
                <tr key={tenant.id}>
                  <td>{tenant.id}</td>
                  <td>{tenant.name}</td>
                  <td>{tenant.domain}</td>
                  <td>{tenant.email}</td>
                  <td>{tenant.phone}</td>
                  <td>
                    <span className={`status-badge status-${getStatusColor(tenant.status)}`}>
                      {tenant.status}
                    </span>
                  </td>
                  <td>{tenant.subscription_plan}</td>
                  <td>{tenant.user_count || 0}</td>
                  <td>{tenant.integration_count || 0}</td>
                  <td>{formatDate(tenant.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Entegrasyonlar */}
      <div className="dashboard-section">
        <h2>Entegrasyonlar</h2>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Ad</th>
                <th>Tip</th>
                <th>Sağlayıcı</th>
                <th>Durum</th>
                <th>Son Senkronizasyon</th>
                <th>Firma</th>
              </tr>
            </thead>
            <tbody>
              {integrations.map((integration) => (
                <tr key={integration.id}>
                  <td>{integration.id}</td>
                  <td>{integration.name}</td>
                  <td>{integration.type}</td>
                  <td>{integration.provider}</td>
                  <td>
                    <span className={`status-badge status-${getStatusColor(integration.status)}`}>
                      {integration.status}
                    </span>
                  </td>
                  <td>{integration.last_sync ? formatDate(integration.last_sync) : 'Hiç'}</td>
                  <td>{integration.tenant_id || 'Sistem'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sistem Logları */}
      <div className="dashboard-section">
        <h2>Son Sistem Logları</h2>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Tarih</th>
                <th>Seviye</th>
                <th>Servis</th>
                <th>Mesaj</th>
                <th>Firma</th>
                <th>Kullanıcı</th>
              </tr>
            </thead>
            <tbody>
              {systemLogs.map((log) => (
                <tr key={log.id} className={`log-row log-${log.level?.toLowerCase()}`}>
                  <td>{formatDate(log.created_at)}</td>
                  <td>
                    <span className={`log-level log-${log.level?.toLowerCase()}`}>
                      {log.level}
                    </span>
                  </td>
                  <td>{log.service}</td>
                  <td>{log.message}</td>
                  <td>{log.tenant_name || 'Sistem'}</td>
                  <td>{log.user_name || 'Sistem'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sistem Ayarları */}
      {systemInfo?.settings && (
        <div className="dashboard-section">
          <h2>Sistem Ayarları</h2>
          <div className="settings-grid">
            {Object.entries(systemInfo.settings).map(([key, value]) => (
              <div key={key} className="setting-item">
                <strong>{key}:</strong> {value}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 